import { FC, memo, useMemo } from 'react'
import { Card, CardProps, Flex, Divider, Stack, HStack } from '@chakra-ui/react'

import { Jobsite, JobsiteFieldsName, JobsiteFilterKeyString, JobsiteKeyString } from 'InstitutionalManagement/types'
import { CustomIconViewer, Id, NoveltyBadge, Status, TextWithCharacterLimit } from 'components'
import { JOBSITE_INITIAL_FILTER_FIELDS } from 'InstitutionalManagement/constants/pages/jobsites'
import { OwnerIcon } from 'components/visualHelps'

type Props = CardProps & {
  hideHeader?: boolean
  onlyFields?: JobsiteFieldsName[]
  jobsiteFilter?: JobsiteFilterKeyString
  jobsite: Jobsite
}

const JobsiteCard: FC<Props> = ({ hideHeader = false, onlyFields, jobsite, jobsiteFilter, children, ...cardProps }) => {
  const jobsiteFilterFields = useMemo(() => {
    return onlyFields || JOBSITE_INITIAL_FILTER_FIELDS
  }, [])

  function setJobsiteValueByKey(key: string): string {
    const jobsiteWithKeyString: JobsiteKeyString = { ...jobsite }
    return (jobsiteWithKeyString[`jobsite_${key}`] || jobsiteWithKeyString[key]) as string
  }

  function setJobsiteFilterValueByKey(key: string): string {
    if (!jobsiteFilter) return ''
    return jobsiteFilter[`Jobsite_${key}`] || jobsiteFilter[key]
  }

  return (
    <Card p={2} width="100%" height="100%" className="accessible" _hover={{ bg: 'lightPrimary' }} {...cardProps}>
      <Stack>
        {!hideHeader && (
          <>
            <Flex alignItems="center" gap={1} justifyContent="space-between">
              <Flex alignItems="center" gap={2}>
                <Id id={setJobsiteValueByKey('id')} />
                <NoveltyBadge date={setJobsiteValueByKey('created_at')} />
              </Flex>
              <Flex alignItems="center" gap={2}>
                <OwnerIcon entity={jobsite} />
                {jobsiteFilterFields.includes('status') && <Status status={setJobsiteValueByKey('status') as string} />}
              </Flex>
            </Flex>
            <Divider color="borderColor" />
          </>
        )}
        <Flex flexWrap="wrap" justify="space-between" gap={2}>
          <HStack>
            <CustomIconViewer
              variant="twoTone"
              boxSize={8}
              color={setJobsiteValueByKey('color')}
              icon={setJobsiteValueByKey('icon')}
            />

            <Stack spacing={0}>
              <TextWithCharacterLimit hasHighlight highlightQuery={setJobsiteFilterValueByKey('name')}>
                {setJobsiteValueByKey('name')}
              </TextWithCharacterLimit>
              <TextWithCharacterLimit
                hasHighlight
                highlightQuery={setJobsiteFilterValueByKey('description')}
                variant="secondary"
              >
                {setJobsiteValueByKey('description')}
              </TextWithCharacterLimit>
            </Stack>
          </HStack>
        </Flex>

        {children && <Divider color="borderColor" />}

        {children}
      </Stack>
    </Card>
  )
}

export default memo(JobsiteCard)
